import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { LabeledCheckbox } from "./LabeledCheckbox";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { Tooltip } from "mds/components/Tooltip";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedCourse } from "store/selectors";
import { getAvServiceFormItems } from "utils/av";

type FormValues = {
  auto_recordings_enabled: boolean;
  av_enabled: boolean;
  av_service: string;
  mute_students_on_main_room_entry: boolean;
};

interface CourseSettingsVirtualClassFormProps {
  className?: string;
  onClose: () => void;
}

export const CourseSettingsVirtualClassForm: FC<CourseSettingsVirtualClassFormProps> = ({
  className,
  onClose,
}) => {
  const currentCourse = useAppSelector(selectDesignatedCourse);

  const form = useForm<FormValues>({
    defaultValues: {
      auto_recordings_enabled: currentCourse?.auto_recordings_enabled,
      av_enabled: currentCourse?.av_enabled,
      av_service: currentCourse?.av_service,
      mute_students_on_main_room_entry: currentCourse?.mute_students_on_main_room_entry,
    },
  });

  if (!currentCourse) {
    return null;
  }

  const avEnabled = form.watch("av_enabled");
  const avService = form.watch("av_service");

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Course Settings - Save changes", BUTTON_EVENT_CATEGORY);
    return storeApi.courses.partial_update({ id: currentCourse.id, ...formData }).then(() => {
      toastSuccessMessage(t("success.toasts.updated_course_settings"));
      onClose();
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-2 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Tooltip text={t("settings.course_settings_av_enabled.tooltip")}>
          <LabeledCheckbox
            eventName="Course Settings - Virtual Classes toggle"
            registration={form.register("av_enabled")}
          >
            {t("settings.course_settings_av_enabled.label")}
          </LabeledCheckbox>
        </Tooltip>

        <Tooltip text={t("settings.course_settings_auto_mute.tooltip")}>
          <LabeledCheckbox
            eventName="Course Settings - Mute students on main room entry toggle"
            registration={form.register("mute_students_on_main_room_entry", {
              disabled: !avEnabled,
            })}
          >
            {t("settings.course_settings_auto_mute.label")}
          </LabeledCheckbox>
        </Tooltip>

        <Tooltip text={t("settings.course_settings_auto_recordings.tooltip")}>
          <LabeledCheckbox
            eventName="Course Settings - Auto record class toggle"
            registration={form.register("auto_recordings_enabled", {
              disabled: !avEnabled,
            })}
          >
            {t("settings.course_settings_auto_recordings.label")}
          </LabeledCheckbox>
        </Tooltip>

        <label className="flex items-center justify-between">
          <Tooltip text={t("settings.course_settings_av_service.tooltip")}>
            <span>{t("settings.course_settings_av_service.label")}</span>
          </Tooltip>
          <input type="hidden" {...form.register("av_service")} />
          <div className="inline-block">
            <DropdownMenu
              disabled={!avEnabled}
              eventName="Course Settings - AV service selection"
              eventPropertyKey="newAvService"
              items={getAvServiceFormItems()}
              selectedValue={avService}
              onSelectionChange={({ id }) => form.setValue("av_service", id)}
            />
          </div>
        </label>

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
